import {FC, useContext, useMemo, useState, useCallback, useEffect} from "react";
import {Box, LinearProgress, Switch, Button} from "@mui/material";
import {
	DataGridPro,
	GridColDef,
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridValueGetterParams,
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	useGridApiRef
} from "@mui/x-data-grid-pro";
import {makeStyles} from "tss-react/mui";
import {CoursesListGridToolbar} from "./Toolbar/CoursesListGridToolbar";
import {useCoursesListGrid} from "./hooks/useCoursesListGrid";
import {useNavigate} from "react-router-dom";
import {
	useToggleCourseBlacklist,
	DEFAULT_COURSES_LIST_PAGE_SIZE,
	PAGE_SIZE_OPTIONS_COURSES_ADMIN
} from "@/app2/shared/data";
import { CourseResponse } from "@/app2/shared/data/gen";
import { App2NotificationContext, App2NotificationSeverity } from "@/app2/shared/contexts/App2NotificationContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";
import {formatDateForDisplay} from "@/app2/shared/utils/dateManipulation";

const useStyles = makeStyles()((theme) => ({
	dataGrid: {
		// Let adminTheme MuiDataGrid.root padding apply (15px 15px 0) so the whole table
		// is inset from its container. Do not set padding: 0.
		// Container border and single/thin dividers also come from adminTheme.
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		},
		"& svg path": {
			fill: theme.palette.common.white
		}
	}
}));

const TABLE_KEY = "courses-grid";
const INITIAL_COLUMN_VISIBILITY: Record<string, boolean> = {updatedAt: false};

interface CourseBlacklistSwitchProps {
	courseId: number;
	isBlacklisted: boolean;
}

const CourseBlacklistSwitch: FC<CourseBlacklistSwitchProps> = ({ courseId, isBlacklisted }) => {
	const { notify } = useContext(App2NotificationContext);
	const toggleBlacklist = useToggleCourseBlacklist();

	return (
		<Switch
			checked={isBlacklisted}
			onClick={(e) => e.stopPropagation()}
			onChange={(e) => {
				toggleBlacklist.mutate(
					{ courseId, state: e.target.checked },
					{
						onSuccess: (data) => {
							if (data) {
								notify("Please note: Currently enrolled users will still have access but it will no longer show up in any users course library, nor will it automatically be assigned.", "Course hidden from team", App2NotificationSeverity.success, 10000);
							} else {
								notify("The course will now appear in users course library and is available for automatic enrollment.", "Course now available", App2NotificationSeverity.success, 10000);
							}
						},
						onError: () => {
							notify("Please try again", "Failed to toggle course", App2NotificationSeverity.error, 5000);
						}
					}
				);
			}}
		/>
	);
};

interface CoursesListGridProps {
	ownedCohortTitle?: string;
}

export const CoursesListGrid: FC<CoursesListGridProps> = ({
	ownedCohortTitle
}) => {
	const {classes} = useStyles();
	const navigate = useNavigate();
	const apiRef = useGridApiRef();

	const defaultFilterSettings = {
		page: 1,
		pageSize: DEFAULT_COURSES_LIST_PAGE_SIZE,
		sortBy: "updatedAt",
		sortDirection: "desc",
		extendedAccess: true
	};
	const {
		const: {filterModel},
		api: {
			assignFilters,
			getPersistedFilterModel,
			handleFilterModelChange
		}} = useCustomDataGrid<CourseResponse>({ 
			defaultFilterSettings, 
			filterStorageKey: "coursesFilterSettings" 
		});	
	const initialFilterSettings = getPersistedFilterModel();
	const {
		courses,
		rowCount,
		isLoading,
		refetch,
		page,
		pageSize,
		sortModel,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		defaultInitialState
	} = useCoursesListGrid({initialFilterSettings, onFilterModelChange: handleFilterModelChange});

	// Column visibility (persisted to localStorage)
	const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(() => {
		const saved = localStorage.getItem(`${TABLE_KEY}-visibility`);
		return saved ? JSON.parse(saved) : INITIAL_COLUMN_VISIBILITY;
	});

	const [layoutModified, setLayoutModified] = useState(false);

	const handleColumnVisibilityChange = useCallback((newModel: Record<string, boolean>) => {
		setColumnVisibilityModel(newModel);
		setLayoutModified(true);
		localStorage.setItem(`${TABLE_KEY}-visibility`, JSON.stringify(newModel));
	}, []);

	// Subscribe to column order and width changes
	useEffect(() => {
		const unsubscribeOrder = apiRef.current.subscribeEvent("columnOrderChange", () => {
			const orderedFields = apiRef.current.getAllColumns().map(col => col.field);
			localStorage.setItem(`${TABLE_KEY}-order`, JSON.stringify(orderedFields));
			setLayoutModified(true);
		});

		const unsubscribeWidth = apiRef.current.subscribeEvent("columnWidthChange", () => {
			const columnWidths: Record<string, number> = {};
			apiRef.current.getAllColumns().forEach(col => {
				if (col.field) {
					columnWidths[col.field] = col.computedWidth || col.width || 100;
				}
			});
			localStorage.setItem(`${TABLE_KEY}-widths`, JSON.stringify(columnWidths));
			setLayoutModified(true);
		});

		if (
			localStorage.getItem(`${TABLE_KEY}-order`) ||
			localStorage.getItem(`${TABLE_KEY}-widths`) ||
			localStorage.getItem(`${TABLE_KEY}-visibility`)
		) {
			setLayoutModified(true);
		}

		return () => {
			unsubscribeOrder();
			unsubscribeWidth();
		};
	}, [apiRef]);

	const resetLayout = useCallback(() => {
		localStorage.removeItem(`${TABLE_KEY}-order`);
		localStorage.removeItem(`${TABLE_KEY}-widths`);
		localStorage.removeItem(`${TABLE_KEY}-visibility`);
		setLayoutModified(false);
		setColumnVisibilityModel(INITIAL_COLUMN_VISIBILITY);
	}, []);

	const columns: GridColDef<CourseResponse>[] = useMemo<GridColDef<CourseResponse>[]>(
		() => [
			{
				...GRID_CHECKBOX_SELECTION_COL_DEF,
				type: "string",
				hideable: false
			},
			{
				field: "title",
				type: "string",
				flex: 1,
				headerName: "Course",
				minWidth: 240
			},
			{
				field: "moduleCount",
				type: "number",
				flex: 1,
				headerName: "Lessons",
				maxWidth: 110,
				valueGetter: (params: GridValueGetterParams<any, CourseResponse>) => {
					return params.row.moduleCount ?? 0;
				}
			},
			{
				field: "enrollmentCount",
				type: "number",
				sortable: false,
				filterable: false,
				flex: 1,
				maxWidth: 110,
				headerName: "Internal Enrollments",
				valueGetter: (params: any) => {
					return params.row.enrollmentCount ?? 0;
				}
			},
			{
				field: "completionCount",
				type: "number",
				filterable: false,
				flex: 1,
				maxWidth: 110,
				headerName: "Course Completions",
				valueGetter: (params: any) => params.row.completionCount ?? 0
			},
			{
				field: "contentType",
				type: "string",
				flex: 1,
				headerName: "Content Type"
			},
			{
				field: "updatedAt",
				type: "date",
				flex: 1,
				headerName: "Last Modified",
				valueGetter: (params: any) =>
					params.row.updatedAt ? new Date(params.row.updatedAt) : null,
				valueFormatter: (params: any) =>
					formatDateForDisplay(params.value as Date | null)
			},
			{
				field: "publishedAt",
				type: "date",
				flex: 1,
				headerName: "Published",
				valueGetter: (params: any) =>
					params.row.publishedAt ? new Date(params.row.publishedAt) : null,
				valueFormatter: (params: any) =>
					formatDateForDisplay(params.value as Date | null)
			},
		{
			field: "authorCohort",
			type: "string",
			flex: 1,
			headerName: "Shared By",
			valueGetter: (params: any) => {
				const authorTitle = params.row.authorCohort?.title ?? "";
				return authorTitle === ownedCohortTitle ? "" : authorTitle;
			}
		},
			{
				field: "isBlacklisted",
				type: "boolean",
				flex: 1,
				sortable: false,
				headerName: "Hide From Team",
				renderCell: (params) => (
					<CourseBlacklistSwitch
						courseId={params.row.id}
						isBlacklisted={params.row.isBlacklisted ?? false}
					/>
				)
			}
		],
		[ownedCohortTitle]
	);

	// Apply saved column order and widths
	const storedColumnOrder: string[] = JSON.parse(localStorage.getItem(`${TABLE_KEY}-order`) || "[]");
	const storedColumnWidths: Record<string, number> = JSON.parse(localStorage.getItem(`${TABLE_KEY}-widths`) || "{}");

	const orderedColumns = storedColumnOrder.length
		? storedColumnOrder.map(field => columns.find(col => col.field === field)).filter(Boolean) as GridColDef<CourseResponse>[]
		: columns;

	const finalColumns = orderedColumns.map((col) => ({
		...col,
		width: storedColumnWidths[col.field] || col.width,
		flex: storedColumnWidths[col.field] ? undefined : col.flex
	}));

	const handleRowClick = (params: any) => {
		navigate(`${params.id}`);
	};

	const Toolbar = useCallback(() => (
		<GridToolbarContainer sx={{pt: 0, pb: 1, px: 2, alignItems: "center"}}>
			<GridToolbarColumnsButton {...({} as any)} />
			<GridToolbarFilterButton {...({} as any)} />
			{layoutModified && (
				<Button size="small" onClick={resetLayout}>
					Reset Layout
				</Button>
			)}
		</GridToolbarContainer>
	), [layoutModified, resetLayout]);

	return (
		<Box>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DataGridPro<CourseResponse>
					apiRef={apiRef}
					autoHeight
					className={classes.dataGrid}
					loading={isLoading}
					sortingMode="server"
					keepNonExistentRowsSelected
					page={page}
					pageSize={pageSize}
					onPageSizeChange={onPageSizeChange}
					onPageChange={onPageChange}
					checkboxSelection
					paginationMode="server"
					pagination
					rowCount={rowCount}
					sortModel={sortModel}
					onSortModelChange={onSortModelChange}
					filterModel={filterModel}
					rows={courses}
					columns={finalColumns.map(e => assignFilters(e as any))}
					filterMode="server"
					onFilterModelChange={onFilterChange}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={handleColumnVisibilityChange}
					components={{Toolbar}}
					rowsPerPageOptions={[...PAGE_SIZE_OPTIONS_COURSES_ADMIN]}
					initialState={defaultInitialState}
					onRowClick={handleRowClick}
					getRowId={(row) => row.id}
					sx={{
						"& .MuiDataGrid-row": {
							cursor: "pointer"
						}
					}}
				/>
			</LocalizationProvider>
			{isLoading && <LinearProgress />}
		</Box>
	);
};
